/**
 * The DOM half of the Email Library's link editor (`LinkTemplateEditor`), kept pure of React so it
 * is unit-tested on its own.
 *
 * Templates are STORED as the Markdown-ish text the proxy renders on send (`lib/mdEmail.js`):
 * plain text, `[text](url)` links, `**bold**`, `- ` bullets, `{tokens}`. The editor SHOWS links as
 * real blue anchors and everything else as the plain text it is. These two functions are the whole
 * contract between the two forms, so a template must survive `renderInto` → `serialize` unchanged.
 */

/** The link syntax `lib/mdEmail.js` turns into an anchor. Keep the two in step. */
const LINK = /\[([^\]\n]+)\]\(([^)\s]+)\)/g;

/**
 * Only these may become a link; anything else stays visible as text. A single `{token}` counts too
 * (Oct 22 2026): `[Stellenbeschreibung]({jobLink})` in a template is a link whose address is filled
 * in per person, and shows as one.
 */
export const ALLOWED_HREF = /^(https?:\/\/|mailto:|\{\w+\}$)/i;

/** Classes for an anchor inside the editor. Exported so a test can assert the look, not just the tag. */
export const LINK_CLASS = 'text-blue-700 underline decoration-blue-300 underline-offset-2 bg-blue-50 rounded-sm';

/** Build one anchor as the editor shows it. */
export function makeLink(doc: Document, text: string, href: string): HTMLAnchorElement {
  const a = doc.createElement('a');
  a.href = href;
  a.setAttribute('data-href', href);
  a.className = LINK_CLASS;
  a.title = href;
  a.textContent = text;
  return a;
}

/** Replace `root`'s content with the stored text: links as anchors, the rest as text nodes. */
export function renderInto(root: HTMLElement, value: string): void {
  const doc = root.ownerDocument;
  root.replaceChildren();
  let last = 0;
  for (const m of value.matchAll(LINK)) {
    const [whole, text, href] = m;
    const at = m.index ?? 0;
    if (at > last) root.appendChild(doc.createTextNode(value.slice(last, at)));
    root.appendChild(ALLOWED_HREF.test(href) ? makeLink(doc, text, href) : doc.createTextNode(whole));
    last = at + whole.length;
  }
  if (last < value.length) root.appendChild(doc.createTextNode(value.slice(last)));
}

/**
 * The editor's content back as stored text.
 *
 * Browsers put line breaks into a contentEditable in three shapes — a `\n` in a text node
 * (`plaintext-only`), a `<br>`, or a block `<div>` — so all three become `\n`. An anchor becomes
 * `[text](href)`; an anchor whose text was deleted down to nothing disappears with it.
 */
export function serialize(root: Node): string {
  let out = '';
  const walk = (node: Node) => {
    for (const child of Array.from(node.childNodes)) {
      if (child.nodeType === 3) {
        out += child.textContent ?? '';
      } else if (child.nodeName === 'BR') {
        out += '\n';
      } else if (child.nodeName === 'A') {
        const el = child as HTMLElement;
        const href = el.getAttribute('data-href') || el.getAttribute('href') || '';
        const text = (el.textContent ?? '').replace(/\n/g, ' ');
        if (text.trim()) out += href ? `[${text}](${href})` : text;
      } else if (child.nodeName === 'DIV' || child.nodeName === 'P') {
        if (out && !out.endsWith('\n')) out += '\n';
        walk(child);
      } else {
        walk(child);
      }
    }
  };
  walk(root);
  // A contentEditable keeps a trailing <br> so an empty last line stays visible; it is not content.
  return out.replace(/\n$/, root.lastChild?.nodeName === 'BR' ? '' : '\n');
}

/** The anchor `node` sits in, if any, inside `root`. */
export function enclosingLink(node: Node | null, root: Node): HTMLAnchorElement | null {
  for (let n: Node | null = node; n && n !== root; n = n.parentNode) {
    if (n.nodeName === 'A') return n as HTMLAnchorElement;
  }
  return null;
}

/**
 * Turn `range` into a link: the selected words if there are any, else a "link text" placeholder.
 * A selection that overlaps an existing link takes over that link rather than nesting a second
 * anchor inside it. Returns the anchor, so the caller can place the caret after it.
 */
export function applyLinkToRange(range: Range, href: string, root: HTMLElement): HTMLAnchorElement {
  const doc = root.ownerDocument;
  const inside = enclosingLink(range.commonAncestorContainer, root);
  if (inside) {
    inside.href = href;
    inside.setAttribute('data-href', href);
    inside.title = href;
    return inside;
  }
  const text = range.toString().replace(/\n/g, ' ') || 'link text';
  const a = makeLink(doc, text, href);
  range.deleteContents();
  range.insertNode(a);
  // Selections that ran across another anchor leave its empty shell behind; drop those.
  for (const empty of Array.from(root.querySelectorAll('a'))) {
    if (empty !== a && !(empty.textContent ?? '').trim()) empty.remove();
  }
  root.normalize();
  return a;
}

/** Remove the link but keep its words. */
export function unwrapLink(a: HTMLAnchorElement): void {
  const parent = a.parentNode;
  if (!parent) return;
  parent.replaceChild(a.ownerDocument.createTextNode(a.textContent ?? ''), a);
  parent.normalize();
}

/** The plain text from the start of `root` to the caret — what the "/" token menu reads. */
export function textBeforeCaret(root: HTMLElement, caretNode: Node, caretOffset: number): string {
  const r = root.ownerDocument.createRange();
  r.setStart(root, 0);
  r.setEnd(caretNode, caretOffset);
  return r.toString();
}

/** How the words a link is being made for look while the URL row is open. */
export const PENDING_CLASS = 'bg-blue-200 rounded-sm ring-1 ring-blue-400';

/**
 * Wrap the selected words in a highlight, so they stay marked while focus is in the URL field —
 * the browser's own selection disappears the moment the field is clicked, and the author is left
 * guessing which words the link is for (Sofia, Oct 8 2026). The span serializes as plain text.
 */
export function markPending(range: Range, root: HTMLElement): HTMLSpanElement {
  const span = root.ownerDocument.createElement('span');
  span.setAttribute('data-pending-link', '');
  span.className = PENDING_CLASS;
  span.appendChild(range.extractContents());
  range.insertNode(span);
  return span;
}

/** The highlighted words become the link. */
export function replacePendingWithLink(span: HTMLSpanElement, href: string, root: HTMLElement): HTMLAnchorElement {
  const text = (span.textContent ?? '').replace(/\n/g, ' ') || 'link text';
  const a = makeLink(root.ownerDocument, text, href);
  span.replaceWith(a);
  for (const empty of Array.from(root.querySelectorAll('a'))) {
    if (empty !== a && !(empty.textContent ?? '').trim()) empty.remove();
  }
  root.normalize();
  return a;
}

/** Cancelled: the words go back to what they were. */
export function clearPending(span: HTMLSpanElement): void {
  const parent = span.parentNode;
  span.replaceWith(...Array.from(span.childNodes));
  parent?.normalize();
}

/** Does the stored text already link to `href`? */
export function hasLinkTo(text: string, href: string): boolean {
  for (const m of text.matchAll(LINK)) if (m[2] === href) return true;
  return false;
}

/**
 * Link the first occurrence of `words` in the stored `text`, case-insensitive, outside any
 * existing link. Returns the new text, or null when the words are not there — the caller then
 * asks the author to pick the words in that language by hand.
 */
export function linkWordsInText(text: string, words: string, href: string): string | null {
  const needle = words.trim().toLowerCase();
  if (!needle) return null;
  let last = 0;
  const segments: { link: boolean; text: string }[] = [];
  for (const m of text.matchAll(LINK)) {
    const at = m.index ?? 0;
    segments.push({ link: false, text: text.slice(last, at) }, { link: true, text: m[0] });
    last = at + m[0].length;
  }
  segments.push({ link: false, text: text.slice(last) });
  for (const seg of segments) {
    if (seg.link) continue;
    const at = seg.text.toLowerCase().indexOf(needle);
    if (at < 0) continue;
    const found = seg.text.slice(at, at + needle.length);
    seg.text = `${seg.text.slice(0, at)}[${found}](${href})${seg.text.slice(at + needle.length)}`;
    return segments.map((x) => x.text).join('');
  }
  return null;
}

